import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { adminSaveLabourTemplate } from "@/lib/api/labour-analyzer.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/_authenticated/portal/admin/labour-templates/new")({
  head: () => ({
    meta: [
      { title: "Admin · New labour-law template" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: NewTemplate,
});

const CATEGORIES = [
  "system_prompt",
  "ccma",
  "lra",
  "bcea",
  "eea",
  "schedule_8",
  "case_law",
  "drafting",
  "general",
];

function NewTemplate() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const save = useServerFn(adminSaveLabourTemplate);

  const [slug, setSlug] = useState("");
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("general");
  const [body, setBody] = useState("");
  const [notes, setNotes] = useState("Initial version");
  const [setActiveOnSave, setSetActive] = useState(true);
  const [saving, setSaving] = useState(false);

  async function onSave() {
    if (slug.length < 2 || title.length < 2 || body.length < 1) {
      toast.error("Fill in slug, title and body.");
      return;
    }
    setSaving(true);
    try {
      const res = await save({
        data: {
          slug,
          title,
          category: category as any,
          body_md: body,
          notes,
          publishAsNewVersion: true,
          setActive: setActiveOnSave,
        },
      });
      toast.success(`Created v${res.version}`);
      qc.invalidateQueries({ queryKey: ["admin-labour-templates"] });
      nav({
        to: "/portal/admin/labour-templates/$id" as any,
        params: { id: res.id } as any,
      });
    } catch (e: any) {
      toast.error(e?.message ?? "Could not save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <Link
        to={"/portal/admin/labour-templates" as any}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-navy"
      >
        <ArrowLeft className="h-4 w-4" /> All templates
      </Link>

      <header className="rounded-2xl border border-border bg-gradient-to-br from-navy/[0.04] to-gold/[0.06] p-6 shadow-soft">
        <h1 className="font-display text-2xl text-navy">New guidance template</h1>
      </header>

      <section className="space-y-3 rounded-xl border border-border bg-card p-5 shadow-soft">
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <Label>Slug</Label>
            <Input
              value={slug}
              onChange={(e) =>
                setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))
              }
              placeholder="e.g. ccma_arbitration_motivation"
              maxLength={80}
            />
          </div>
          <div>
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c.replaceAll("_", " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label>Title</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
        </div>

        <div>
          <Label>Body (Markdown)</Label>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={20}
            className="font-mono text-xs"
            maxLength={60000}
          />
        </div>

        <div>
          <Label>Version notes</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} />
        </div>

        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={setActiveOnSave}
            onChange={(e) => setSetActive(e.target.checked)}
          />
          Make this the active version immediately
        </label>

        <Button onClick={onSave} disabled={saving} className="bg-navy hover:bg-navy/90">
          {saving ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Create template
        </Button>
      </section>
    </div>
  );
}
